/**
 * Workflow Node Registry — Single Source of Truth
 *
 * Zod schemas for each node type's Omega-format parameters + UI metadata.
 * Everything else derives from this registry:
 * - NODE_DEFINITIONS / ADDABLE_NODE_DEFINITIONS for the NodePicker UI
 * - Default parameters and validation helpers for graph operations
 */

import type { TFunction } from "i18next";
import { z } from "zod/v3";
import {
  AGENCY_ZOOM_CUSTOMER_STATUS_VALUES,
  AGENCY_ZOOM_ORDER_VALUES,
} from "@/components/workflows/custom-nodes/agency-zoom/search-customers/customerSearchOptions";
import {
  AGENCY_ZOOM_LEAD_SOLD_PERIOD_VALUES,
  AGENCY_ZOOM_LEAD_SORT_VALUES,
  AGENCY_ZOOM_LEAD_STATUS_VALUES,
} from "@/components/workflows/custom-nodes/agency-zoom/search-leads/leadSearchOptions";
import {
  AGENCY_ZOOM_TASK_CATEGORY_VALUES,
  AGENCY_ZOOM_TASK_PERIOD_VALUES,
  AGENCY_ZOOM_TASK_STATUS_VALUES,
} from "@/components/workflows/custom-nodes/agency-zoom/search-tasks/taskSearchOptions";
import { AGENCY_ZOOM_TASK_CREATE_TYPE_VALUES } from "@/components/workflows/custom-nodes/agency-zoom/create-task/taskCreateOptions";
import { AGENCY_ZOOM_MARITAL_STATUS_VALUES } from "@/components/workflows/custom-nodes/agency-zoom/create-lead/leadCreateOptions";
import {
  MOMENTUM_TASK_PRIORITY_VALUES,
  MOMENTUM_TASK_STATUS_VALUES,
} from "@/components/workflows/custom-nodes/momentum/tasks/momentumTaskOptions";
import { NODE_TYPE_KEYS } from "@/components/workflows/custom-nodes/nodeTypes";
import { CONTENT_FORMATS } from "@/components/workflows/custom-nodes/web-search/webSearchConstants";
import { CELL_VALUES_OPTIONS } from "@/components/workflows/custom-nodes/excel/types";
import type { IconName } from "@fortawesome/fontawesome-common-types";

// =============================================================================
// OmegaParameter Zod Schemas
// =============================================================================

/** Literal value: { type: "literal", value: T } */
const omegaLiteral = <T extends z.ZodType>(valueSchema: T) =>
  z.object({ type: z.literal("literal"), value: valueSchema });

/** CEL expression reference: { type: "cel", value: string } */
const omegaCel = z.object({ type: z.literal("cel"), value: z.string() });

/**
 * Sensitive `[SecretField]` input: Omega advertises these as cel-only and
 * rejects plaintext literals at publish time (the value must reference a
 * `secret.*`). The builder enforces the same via the secret picker + validation.
 */
const omegaSecret = omegaCel;

/** String template with embedded expressions: { type: "template", value: string } */
const omegaTemplate = z.object({
  type: z.literal("template"),
  value: z.string(),
});

/** OmegaParameter<T> — union of literal, cel, and template */
const omegaParam = <T extends z.ZodType>(valueSchema: T) =>
  z.union([omegaLiteral(valueSchema), omegaCel, omegaTemplate]);

/** OmegaParameter<string> shorthand */
const omegaString = omegaParam(z.string());

/** OmegaParameter<string> for fields Omega validates as uuid */
const omegaUuid = omegaParam(z.string().uuid());

/** Literal-only UUID — design-time IDs Omega rejects as CEL at compile time */
const omegaLiteralUuid = omegaLiteral(z.string().uuid());

/** OmegaParameter<number> shorthand */
const omegaNumber = omegaParam(z.number());

/** OmegaParameter<number> with SFTP default port (22) and valid port constraints */
const omegaSftpPort = omegaParam(z.number().int().min(1).max(65535).default(22));

/** Omega FilePointer literal schema — UI forces CEL mode; this default is a placeholder for draft saves */
const omegaFilePointer = omegaParam(
  z
    .object({ storage_path: z.string(), media_type: z.string() })
    .default({ storage_path: "", media_type: "" })
);

/** Shared SFTP connection parameters schema (host, port, username, remotePath, password, privateKey) */
const sftpConnectionSchema = {
  host: omegaString,
  port: omegaSftpPort,
  username: omegaString,
  remotePath: omegaString,
  password: omegaSecret.optional(),
  privateKey: omegaSecret.optional(),
};

/** Every AgencyZoom id is an integer parameter. */
const omegaAgencyZoomInt = omegaParam(z.number().int());

/**
 * A required AgencyZoom picker id. `0` is the picker's "nothing selected", which
 * Omega's `MustSelect` rejects, so it never passes here either.
 */
const omegaAgencyZoomSelectedId = omegaParam(z.number().int().min(1));

/**
 * An optional assignment id. Omega drops a `0` from the upstream request, so
 * only a negative is wrong.
 */
const omegaAgencyZoomAssignmentId = omegaParam(z.number().int().min(0));

/** Per-item parameters, or one whole-list CEL expression. */
const omegaIdList = z.union([z.array(omegaAgencyZoomInt), omegaCel]);

/** `ValueList<Guid>`: per-item GUIDs, or one CEL expression for the whole list. */
const omegaGuidList = z.union([z.array(omegaUuid), omegaCel]);

const omegaMomentumTaskStatus = omegaParam(z.enum(MOMENTUM_TASK_STATUS_VALUES));
const omegaMomentumTaskPriority = omegaParam(z.enum(MOMENTUM_TASK_PRIORITY_VALUES));

/** The optional task fields `momentum_create_task` and `momentum_update_task` share. */
const momentumTaskFieldsShape = {
  description: omegaString.optional(),
  dueDate: omegaString.optional(),
  status: omegaMomentumTaskStatus.optional(),
  priority: omegaMomentumTaskPriority.optional(),
  categoryName: omegaString.optional(),
  assigneeAgentIds: omegaGuidList.optional(),
};

/** Custom fields; `fieldValue` is always an array. */
const omegaAgencyZoomCustomFields = z.union([
  z.array(
    omegaParam(
      z.object({ fieldName: z.string(), fieldValue: z.array(z.string()) })
    )
  ),
  omegaCel,
]);

/**
 * Parameters shared by both AgencyZoom create-lead tiles, all optional.
 * `firstname`, `pipelineId` and `stageId` are absent: each tile declares its own.
 */
const agencyZoomLeadSharedShape = {
  email: omegaString.optional(),
  country: omegaString.optional(),
  leadSourceId: omegaAgencyZoomAssignmentId.optional(),
  assignTo: omegaAgencyZoomAssignmentId.optional(),
  lastname: omegaString.optional(),
  middlename: omegaString.optional(),
  nickname: omegaString.optional(),
  phone: omegaString.optional(),
  secondaryEmail: omegaString.optional(),
  secondaryPhone: omegaString.optional(),
  streetAddress: omegaString.optional(),
  streetAddressLine2: omegaString.optional(),
  city: omegaString.optional(),
  state: omegaString.optional(),
  zip: omegaString.optional(),
  birthday: omegaString.optional(),
  notes: omegaString.optional(),
  agencyNumber: omegaString.optional(),
  departmentCode: omegaString.optional(),
  groupCode: omegaString.optional(),
  tagNames: omegaString.optional(),
  contactDate: omegaString.optional(),
  soldDate: omegaString.optional(),
  quoteDate: omegaString.optional(),
  xDate: omegaString.optional(),
  nextExpirationDate: omegaString.optional(),
  maritalStatus: omegaParam(z.enum(AGENCY_ZOOM_MARITAL_STATUS_VALUES)).optional(),
  csrId: omegaAgencyZoomAssignmentId.optional(),
  assignmentGroupId: omegaAgencyZoomAssignmentId.optional(),
  otherProducers: omegaIdList.optional(),
  otherCsrs: omegaIdList.optional(),
  customFields: omegaAgencyZoomCustomFields.optional(),
};

/** Parameters shared by the row-parsing file nodes (parse_csv, parse_excel). */
const parseRowsParametersObject = z.object({
  file: omegaFilePointer,
  hasHeader: omegaParam(z.boolean().default(true)),
  columns: z.union([z.array(omegaString), omegaCel]).optional(),
  skipRows: omegaParam(z.number().int().min(0)).optional(),
  maxRows: omegaParam(z.number().int().min(0).max(10_000)).optional(),
  filter: omegaCel.optional(),
});

function refineColumnsWhenNoHeader(
  value: z.infer<typeof parseRowsParametersObject>,
  ctx: z.RefinementCtx
) {
  if (
    value.hasHeader.type === "literal" &&
    value.hasHeader.value === false &&
    !value.columns
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["columns"],
      message: "'Columns' is required when 'Has header' is false.",
    });
  }
}

const parseCsvParametersSchema = parseRowsParametersObject.superRefine(
  refineColumnsWhenNoHeader
);

const parseExcelParametersSchema = parseRowsParametersObject
  .extend({
    sheet: omegaParam(z.string().min(1)).optional(),
    startRow: omegaParam(z.number().int().min(1)).optional(),
    cellValues: omegaParam(z.enum(CELL_VALUES_OPTIONS)).optional(),
  })
  .superRefine(refineColumnsWhenNoHeader);

/** Omega's Map.MaxItems — hard cap on the items list. */
const MAP_MAX_ITEMS = 10_000;

/**
 * Mirror of Omega's `CelFieldName.Normalize`: separators → underscores, strip
 * non-alphanumerics, snake_case, collapse/trim underscores. Used to flag
 * projection field names that normalize to empty or collide — the same checks
 * Omega runs at publish time.
 */
function normalizeCelFieldName(name: string): string {
  const snaked = name
    .replace(/[\s\-.]+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    // Acronym boundary (e.g. "HTTPRequest" → "HTTP_Request"). A non-consuming
    // lookahead avoids the `[A-Z]+[A-Z]` quantifier overlap that makes the
    // naive form super-linear (ReDoS-prone).
    .replace(/([A-Z])(?=[A-Z][a-z])/g, "$1_")
    .toLowerCase();

  // Collapse runs of underscores and trim leading/trailing ones without an
  // anchored-alternation regex: split on `_`, drop empty segments, rejoin.
  return snaked.split("_").filter(Boolean).join("_");
}

/**
 * Map projection expressions are evaluated per item inside the activity, where
 * only `node.item` and `node.index` exist — the workflow run context does not
 * cross the activity boundary. Returns every reference in `expression` that
 * Omega's Map.CheckProjectionReferences would reject: `node.<id>` for any id
 * other than item/index, and the run-scoped `config.*`/`sys.*`/`secret.*`
 * namespaces.
 */
function findForbiddenProjectionReferences(expression: string): string[] {
  const forbidden: string[] = [];
  for (const match of expression.matchAll(/\bnode\.([\w-]+)/g)) {
    if (match[1] !== "item" && match[1] !== "index") {
      forbidden.push(`node.${match[1]}`);
    }
  }
  for (const match of expression.matchAll(/\b(config|sys|secret)\.[\w-]+/g)) {
    forbidden.push(match[0]);
  }
  return forbidden;
}

// =============================================================================
// Registry Entry Interface
// =============================================================================

/**
 * Describes the runtime variables a container node injects onto its
 * inner nodes by virtue of being inside the container.
 *
 * Each variable is referenced from inside the container's inner graph as:
 *   `node.<container-id>.<variable.name>`
 *
 * Example — `foreach` declares
 *   `{ name: "_each_.item", type: "object", description: "Current item" }`
 * so an inner node can write `node.fe_loop._each_.item` in CEL or
 * `{{ node.fe_loop._each_.item }}` in templates.
 *
 * The builder reads this field: `getAvailableVariables` injects the
 * variables onto every inner node so they show up in the @ mention menu.
 *
 * Adding a new iteration-style container = set `isContainer: true` and
 * declare `containerScope`. No further builder-UI plumbing is required
 * for the iteration-scope side. (Graph-op dispatch and the
 * `isContainerOmegaNode` TS guard still need the type listed there —
 * see "Container Node Invariants" in `.cursor/rules/workflows.mdc`.)
 */
export interface ContainerScope {
  variables: Array<{
    name: string;
    type: "string" | "number" | "boolean" | "object" | "array";
    description: string;
  }>;
}

interface NodeRegistryEntry {
  /** Display label for the node */
  label: string;
  /** FontAwesome icon name (used as a fallback when `iconUrl` is not set) */
  icon: IconName;
  /**
   * Optional path to a brand SVG under `public/`. When set, UI consumers
   * (NodePicker, on-canvas suggestion handles, node header) render this
   * image instead of the FontAwesome `icon`. Use for third-party brand
   * marks (e.g. Google Drive, Slack) where a colored logo conveys more
   * than a generic glyph.
   */
  iconUrl?: string;
  /** Short description of what the node does */
  description: string;
  /** Zod schema for the node's Omega-format parameters */
  parametersSchema?: z.ZodObject<z.ZodRawShape> | z.ZodEffects<z.ZodObject<z.ZodRawShape>>;
  /** Trigger node — no incoming edges, max 1 per workflow (e.g., start) */
  isTrigger?: boolean;
  /** Terminal node — no outgoing edges (e.g., end) */
  isTerminal?: boolean;
  /** Has onFailureEdgeId — two outgoing edges: success + failure */
  hasFailureRoute?: boolean;
  /** Branching node — multiple outgoing edges via branches (e.g., split) */
  isBranching?: boolean;
  /** Only visible to super admins in the UI */
  superAdminOnly?: boolean;
  /** Placeholder node — not user-addable (e.g., noop/ghost) */
  isPlaceholder?: boolean;
  /** Container node — holds an inner graph (e.g., sub_graph) */
  isContainer?: boolean;
  /**
   * For containers that inject runtime-scoped variables onto their
   * inner nodes (e.g. `foreach`'s `_each_.item` / `_each_.index`).
   * Read by the builder UI — see `ContainerScope` above.
   */
  containerScope?: ContainerScope;
}

// =============================================================================
// Node Registry
// =============================================================================

export const NODE_REGISTRY: Record<string, NodeRegistryEntry> = {
  start: {
    label: "Start",
    icon: "play",
    description: "Workflow entry point",
    isTrigger: true,
    parametersSchema: z.object({
      schedule: z
        .object({
          cronExpression: z.string(),
          timezone: z.string().optional(),
          overlap: z.string().optional(),
        })
        .optional(),
      webhook: z
        .object({
          isEnabled: z.boolean(),
        })
        .optional(),
      inputSchema: z.record(z.unknown()).optional(),
      metadata: z
        .object({
          toolId: z.string().optional(),
          exposeAsTool: z.boolean().optional(),
          toolStartMode: z.enum(["async", "sync"]).optional(),
          acceptFiles: z.boolean().optional(),
          gailWebhookEvents: z.array(z.enum(["call_processed", "call_failed"])).optional(),
        })
        .optional(),
    }),
  },

  end: {
    label: "End",
    icon: "stop",
    description: "Workflow end point",
    isTerminal: true,
  },

  delay: {
    label: "Delay",
    icon: "clock",
    description: "Add time delay",
    parametersSchema: z.object({
      until: omegaString,
    }),
  },

  random_number: {
    label: "Random Number",
    icon: "dice",
    description: "Generate random value",
    parametersSchema: z.object({
      min: omegaNumber,
      max: omegaNumber,
    }),
  },

  split: {
    label: "Conditional",
    icon: "code-branch",
    description: "Branch workflow based on conditions",
    isBranching: true,
    parametersSchema: z.object({
      branches: z.array(
        z.object({
          label: z.string().optional(),
          check: omegaCel,
          edgeId: z.string(),
        })
      ),
      defaultEdgeId: z.string(),
    }),
  },

  check: {
    label: "Check",
    icon: "check",
    description: "Evaluate conditions and return an outcome label",
    parametersSchema: z.object({
      conditions: z.array(omegaParam(z.boolean())),
      outcomes: z.array(z.string()),
      defaultOutcome: z.string(),
    }),
  },

  http_request: {
    label: "HTTP Request",
    icon: "globe",
    description: "Make an HTTP request",
    parametersSchema: z.object({
      url: omegaString,
      method: omegaString,
      headers: z.record(omegaString).optional(),
      queryParams: z.record(omegaString).optional(),
      body: omegaString.optional(),
    }),
  },

  send_email: {
    label: "Send Email",
    icon: "envelope",
    description: "Send an email message",
    hasFailureRoute: true,
    parametersSchema: z.object({
      to: omegaString,
      subject: omegaString,
      body: omegaString,
      htmlContent: omegaString.optional(),
      attachmentName: omegaString.optional(),
      attachmentBase64: omegaString.optional(),
      fileAttachments: z
        .union([
          z.array(omegaParam(z.object({ storage_path: z.string(), media_type: z.string() }))),
          omegaCel,
        ])
        .nullable()
        .optional(),
    }),
  },

  send_sms: {
    label: "Send SMS",
    icon: "message" as IconName,
    description: "Send an SMS text message",
    hasFailureRoute: true,
    parametersSchema: z.object({
      to: omegaString,
      message: omegaString,
    }),
  },

  create_file: {
    label: "Create Text File",
    icon: "file-circle-plus" as IconName,
    description: "Create a file from text or binary content",
    hasFailureRoute: true,
    parametersSchema: z.object({
      content: omegaString,
      fileName: omegaString,
      mediaType: omegaString,
    }),
  },

  parse_csv: {
    label: "Parse CSV",
    icon: "file-csv" as IconName,
    description: "Parse a CSV file into rows for downstream nodes",
    hasFailureRoute: true,
    parametersSchema: parseCsvParametersSchema,
  },

  parse_excel: {
    label: "Parse Excel",
    icon: "file-excel" as IconName,
    description: "Parse one sheet of an Excel (.xlsx) workbook into rows for downstream nodes",
    hasFailureRoute: true,
    parametersSchema: parseExcelParametersSchema,
  },

  slice_csv: {
    label: "Slice CSV",
    icon: "file-csv" as IconName,
    description: "Split a CSV file into smaller row-capped slices",
    hasFailureRoute: true,
    parametersSchema: z.object({
      file: omegaFilePointer,
      hasHeader: omegaParam(z.boolean().default(true)),
      maxRows: omegaParam(z.number().int().min(0)).optional(),
    }),
  },

  create_csv: {
    label: "Create CSV",
    icon: "file-csv" as IconName,
    description: "Build a CSV file from rows produced by upstream nodes",
    hasFailureRoute: true,
    parametersSchema: z
      .object({
        rows: omegaParam(z.array(z.unknown())),
        fileName: omegaString,
        includeHeader: omegaParam(z.boolean()).optional(),
        fields: z.union([z.array(omegaString), omegaCel]).optional(),
        headers: z.union([z.array(omegaString), omegaCel]).optional(),
        skipRows: omegaParam(z.number().int().min(0)).optional(),
        maxRows: omegaParam(z.number().int().min(0).max(10_000)).optional(),
        filter: omegaCel.optional(),
      })
      .superRefine((value, ctx) => {
        // Mirror Omega's ValidateFieldsHeadersLengthMatch — only when both
        // are literal arrays, since CEL refs resolve at runtime.
        if (
          Array.isArray(value.fields) &&
          Array.isArray(value.headers) &&
          value.fields.length !== value.headers.length
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["headers"],
            message:
              "'Fields' and 'Headers' must have the same length when both are provided.",
          });
        }
      }),
  },

  start_outbound_call: {
    label: "Outbound Call",
    icon: "phone",
    description: "Make an outbound call",
    parametersSchema: z.object({
      to: omegaString,
      script: omegaParam(
        z.object({
          name: z.string(),
          greeting: z.string(),
          greetingMode: z.enum(["static", "prompt", "random", "time_specific"]).optional(),
          greetingPrompt: z.string().optional(),
          greetingVariants: z.array(z.string()).optional(),
          greetingMorning: z.string().optional(),
          greetingAfternoon: z.string().optional(),
          greetingNight: z.string().optional(),
          greetingMorningStart: z.string().optional(),
          greetingAfternoonStart: z.string().optional(),
          greetingNightStart: z.string().optional(),
          objective: z.string(),
          policy: z.string(),
          voicemail: z.string(),
          goodbye: z.string(),
        })
      ),
    }),
  },

  pipe: {
    label: "Data Transform",
    icon: "shuffle",
    description: "Transform data into variables",
    parametersSchema: z.object({
      data: z.record(omegaParam(z.unknown())),
    }),
  },

  add_contact: {
    label: "Add Contact",
    icon: "user-plus",
    description: "Add a new contact to the system",
    hasFailureRoute: true,
    parametersSchema: z.object({
      firstName: omegaString,
      lastName: omegaString,
      emails: z.array(omegaString),
      phoneNumbers: z.array(omegaParam(z.object({ number: z.string(), type: z.string() }))),
      businessName: omegaString,
      additionalData: z.record(omegaParam(z.unknown())),
    }),
  },

  add_bulk_contacts: {
    label: "Add Bulk Contacts",
    icon: "users" as IconName,
    description:
      "Bulk-create Gail contacts from a list of rows (CSV-friendly, success/failure branches)",
    hasFailureRoute: true,
    parametersSchema: z.object({
      // `contacts` is Omega's ValueList<ContactItem>. The array form is the
      // schema default; the whole-list CEL reference form is also valid and
      // handled by the transformer.
      contacts: z.array(
        omegaParam(
          z.object({
            firstName: z.string(),
            lastName: z.string(),
            email: z.string(),
            phone: z.string(),
            phoneType: z.string().optional(),
            businessName: z.string(),
            additionalData: z.record(z.unknown()).optional(),
          })
        )
      ),
      dedupe: omegaParam(z.boolean()).optional(),
    }),
  },

  create_contact_list: {
    label: "Create Contact List",
    icon: "list" as IconName,
    description: "Create a new contact list in Gail",
    hasFailureRoute: true,
    parametersSchema: z.object({
      listName: omegaString,
      description: omegaString,
    }),
  },

  start_campaign: {
    label: "Start Campaign",
    icon: "play",
    description: "Start a campaign (success/failure branches)",
    hasFailureRoute: true,
    parametersSchema: z.object({
      campaignId: omegaUuid,
    }),
  },

  create_campaign: {
    label: "Create Campaign",
    icon: "bullhorn",
    description: "Clone a template campaign (success/failure branches)",
    hasFailureRoute: true,
    parametersSchema: z.object({
      templateCampaignId: omegaUuid,
      name: omegaString.optional(),
      status: omegaString.optional(),
    }),
  },

  add_contact_to_campaign: {
    label: "Add Contact to Campaign",
    icon: "bullhorn",
    description: "Add contact to a campaign",
    hasFailureRoute: true,
    parametersSchema: z.object({
      campaignId: omegaUuid,
      // Default to empty CEL so the settings panel opens in variable mode
      // instead of showing a naked empty GUID in static text.
      contactId: omegaUuid.default({ type: "cel", value: "" }),
    }),
  },

  add_contacts_to_campaign_bulk: {
    label: "Add Contacts to Campaign (Bulk)",
    icon: "bullhorn",
    description: "Add one or more existing contacts to a campaign in bulk",
    hasFailureRoute: true,
    parametersSchema: z.object({
      campaignId: omegaUuid,
      contactIds: z.union([z.array(omegaUuid).min(1).max(1000), omegaCel]),
    }),
  },

  add_contact_to_contact_list: {
    label: "Add Contact to Contact List",
    icon: "address-book" as IconName,
    description: "Add one or more contacts to a contact list",
    hasFailureRoute: true,
    parametersSchema: z.object({
      contactListId: omegaUuid,
      contactIds: z.union([z.array(omegaUuid).min(1).max(2500), omegaCel]),
    }),
  },

  get_call_data: {
    label: "Get Call Data",
    icon: "phone-arrow-down-left" as IconName,
    description: "Retrieve call data by SID from Gail",
    hasFailureRoute: true,
    parametersSchema: z.object({
      callSid: omegaString,
    }),
  },

  download_call_recording: {
    label: "Download Call Recording",
    icon: "arrow-down-to-square" as IconName,
    description: "Download a Gail call recording into workflow file storage",
    hasFailureRoute: true,
    parametersSchema: z.object({
      callSid: omegaString,
    }),
  },

  slack_direct_message: {
    label: "Slack DM",
    icon: "comment",
    description: "Send a Slack direct message",
    parametersSchema: z.object({
      recipient: omegaString,
      message: omegaString,
      botToken: omegaSecret,
    }),
  },

  slack_post_channel_message: {
    label: "Slack Channel Message",
    icon: "comment",
    description: "Post a message to a Slack channel",
    parametersSchema: z.object({
      channel: omegaString,
      message: omegaString,
      botToken: omegaSecret,
    }),
  },

  chaos: {
    label: "Chaos",
    icon: "bolt",
    description: "Random failure for testing",
    hasFailureRoute: true,
    superAdminOnly: true,
    parametersSchema: z.object({
      chanceToFail: omegaNumber,
    }),
  },

  web_search: {
    label: "Web Search",
    icon: "magnifying-glass" as IconName,
    description: "Search the web and retrieve results",
    hasFailureRoute: true,
    parametersSchema: z.object({
      query: omegaString,
      limit: omegaParam(z.number().int().min(1).max(10)).optional(),
      contentFormat: omegaParam(z.enum(CONTENT_FORMATS)).optional(),
    }),
  },

  web_extract: {
    label: "Web Extract",
    icon: "magnifying-glass-arrow-right" as IconName,
    description: "Extract structured data from web pages",
    hasFailureRoute: true,
    parametersSchema: z
      .object({
        // Backend requires every nullable WebResource field to be present with
        // an explicit null (RespectRequiredConstructorParameters=true), so the
        // schema uses nullable() rather than optional() to reflect the wire shape.
        sources: z.union([
          z
            .array(
              omegaParam(
                z.object({
                  url: z.string(),
                  title: z.string().nullable(),
                  description: z.string().nullable(),
                  content: z.string().nullable(),
                })
              )
            )
            .min(1)
            .max(10),
          omegaCel,
        ]),
        prompt: omegaString.optional(),
        schema: omegaString.optional(),
      })
      .superRefine((value, ctx) => {
        const isEmptyOmegaString = (fieldValue: unknown) => {
          if (fieldValue == null) return true;
          if (typeof fieldValue === "string") return fieldValue.trim().length === 0;
          if (
            typeof fieldValue === "object" &&
            "value" in fieldValue &&
            typeof (fieldValue as { value: unknown }).value === "string"
          ) {
            return (fieldValue as { value: string }).value.trim().length === 0;
          }
          return false;
        };
        if (isEmptyOmegaString(value.prompt) && isEmptyOmegaString(value.schema)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["prompt"],
            message: "Either prompt or schema is required.",
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["schema"],
            message: "Either prompt or schema is required.",
          });
        }
      }),
  },

  agent: {
    label: "Agent",
    icon: "robot" as IconName,
    description: "Use AI to process data and return structured output",
    hasFailureRoute: true,
    parametersSchema: z.object({
      prompt: omegaString,
      // ToolsObjectSchema (raw on UI side; transformer wraps it in literal for the wire).
      desiredOutputFields: z.object({
        type: z.literal("object").default("object"),
        description: z.string().default(""),
        properties: z.record(z.unknown()).default({}),
        required: z.array(z.string()).default([]),
      }),
      agentInput: omegaString.optional(),
    }),
  },

  run_skill: {
    label: "Run Skill",
    icon: "wand-magic-sparkles" as IconName,
    description: "Execute a cataloged Agent Skill in an isolated sandbox",
    hasFailureRoute: true,
    parametersSchema: z.object({
      skillId: omegaLiteralUuid,
      instruction: omegaString,
      files: z.array(omegaCel).optional(),
    }),
  },

  document_analysis: {
    label: "Document Analysis",
    icon: "file-lines" as IconName,
    description: "Analyze uploaded files to identify document types and extract data",
    hasFailureRoute: true,
    parametersSchema: z.object({
      files: omegaCel,
      targetItems: z.array(
        z.object({
          documentType: z.string(),
          description: z.string(),
          fields: z
            .array(
              z.object({
                fieldName: z.string(),
                description: z.string(),
              })
            )
            .optional(),
        })
      ),
    }),
  },

  jotform_submit_form: {
    label: "JotForm Submit",
    icon: "file-signature" as IconName,
    description: "Submit answers to a JotForm form",
    hasFailureRoute: true,
    parametersSchema: z.object({
      formId: omegaString,
      apiKey: omegaSecret,
      // KeyValuePairWithExpression[] — UI keeps key/value pairs ordered;
      // transformer converts to a record on the wire.
      answers: z.array(z.object({ key: z.string(), value: omegaString })),
    }),
  },

  mcp_call_tool: {
    label: "MCP Tool",
    icon: "plug" as IconName,
    description: "Call a tool on a remote MCP server",
    hasFailureRoute: true,
    parametersSchema: z.object({
      // Exactly one of serverUrl and credentialId.
      serverUrl: omegaString.optional(),
      credentialId: omegaString.optional(),
      toolName: omegaString,
      // Literals keep their JSON type; the transformer turns the record into
      // ordered rows for the editor.
      arguments: z.record(omegaParam(z.unknown())),
      headers: z.record(omegaString).optional(),
    }),
  },

  sftp_upload_file: {
    label: "SFTP Upload",
    icon: "server" as IconName,
    description: "Upload a file to an SFTP server",
    hasFailureRoute: true,
    parametersSchema: z.object({
      ...sftpConnectionSchema,
      file: omegaFilePointer,
    }),
  },

  sftp_download_file: {
    label: "SFTP Download",
    icon: "server" as IconName,
    description: "Download a file from an SFTP server",
    hasFailureRoute: true,
    parametersSchema: z.object({
      ...sftpConnectionSchema,
    }),
  },

  sftp_list_directory: {
    label: "SFTP List Directory",
    icon: "server" as IconName,
    description: "List files in an SFTP directory",
    hasFailureRoute: true,
    parametersSchema: z.object({
      ...sftpConnectionSchema,
      namePattern: omegaString.optional(),
      modifiedAfter: omegaString.optional(),
      modifiedBefore: omegaString.optional(),
      includeDirectories: omegaParam(z.boolean()).optional(),
      sortBy: omegaString.optional(),
      sortDescending: omegaParam(z.boolean()).optional(),
      maxResults: omegaNumber.optional(),
    }),
  },

  google_drive_list_files: {
    label: "Google Drive List Files",
    icon: "hard-drive" as IconName,
    iconUrl: "/icons/integrations/google-drive.svg",
    description: "List files and folders from Google Drive",
    hasFailureRoute: true,
    parametersSchema: z.object({
      folderId: omegaString.optional(),
      namePattern: omegaString.optional(),
      mimeType: omegaString.optional(),
      modifiedAfter: omegaString.optional(),
      modifiedBefore: omegaString.optional(),
      includeFolders: omegaParam(z.boolean()).optional(),
      includeTrashed: omegaParam(z.boolean()).optional(),
      orderBy: omegaString.optional(),
      orderDescending: omegaParam(z.boolean()).optional(),
      maxResults: omegaParam(z.number().int().min(1).max(1000)).optional(),
    }),
  },

  google_drive_download_file: {
    label: "Google Drive Download File",
    icon: "hard-drive" as IconName,
    iconUrl: "/icons/integrations/google-drive.svg",
    description: "Download a file from Google Drive into workflow storage",
    hasFailureRoute: true,
    parametersSchema: z.object({
      fileId: omegaString,
      exportMediaType: omegaString.optional(),
    }),
  },

  google_drive_upload_file: {
    label: "Google Drive Upload File",
    icon: "hard-drive" as IconName,
    iconUrl: "/icons/integrations/google-drive.svg",
    description: "Upload a workflow file into Google Drive",
    hasFailureRoute: true,
    parametersSchema: z.object({
      file: omegaFilePointer,
      fileName: omegaString,
      folderId: omegaString.optional(),
      mediaType: omegaString.optional(),
    }),
  },

  google_sheets_append_rows: {
    label: "Google Sheets Append Rows",
    icon: "hard-drive" as IconName,
    iconUrl: "/icons/integrations/google-drive.svg",
    description: "Append rows to a Google Sheet by matching column headers",
    hasFailureRoute: true,
    parametersSchema: z.object({
      spreadsheetId: omegaString,
      rows: z.union([
        omegaLiteral(z.array(z.record(z.unknown()))),
        omegaCel,
      ]),
      sheetName: omegaString.optional(),
    }),
  },

  agencyzoom_search_customers: {
    label: "AgencyZoom Search Customers",
    icon: "users" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Search AgencyZoom customers with optional filters",
    hasFailureRoute: true,
    parametersSchema: z.object({
      page: omegaParam(z.number().int().min(0)).optional(),
      pageSize: omegaParam(z.number().int().min(1).max(100)).optional(),
      sort: omegaString.optional(),
      order: omegaParam(z.enum(AGENCY_ZOOM_ORDER_VALUES)).optional(),
      firstname: omegaString.optional(),
      lastname: omegaString.optional(),
      email: omegaString.optional(),
      phone: omegaString.optional(),
      state: omegaString.optional(),
      city: omegaString.optional(),
      startDate: omegaString.optional(),
      endDate: omegaString.optional(),
      leadSourceId: omegaParam(z.number().int()).optional(),
      agentId: omegaParam(z.number().int()).optional(),
      status: omegaParam(z.enum(AGENCY_ZOOM_CUSTOMER_STATUS_VALUES)).optional(),
    }),
  },

  agencyzoom_get_customer: {
    label: "AgencyZoom Get Customer",
    icon: "user" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Fetch a single AgencyZoom customer by ID",
    hasFailureRoute: true,
    parametersSchema: z.object({
      customerId: omegaParam(z.number().int().positive()),
    }),
  },

  agencyzoom_add_customer_note: {
    label: "AgencyZoom Add Customer Note",
    icon: "note-sticky" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Add a note to an AgencyZoom customer",
    hasFailureRoute: true,
    parametersSchema: z.object({
      customerId: omegaParam(z.number().int().positive()),
      note: omegaString,
    }),
  },

  agencyzoom_search_leads: {
    label: "AgencyZoom Search Leads",
    icon: "users" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Search AgencyZoom leads with optional filters",
    hasFailureRoute: true,
    parametersSchema: z.object({
      page: omegaParam(z.number().int().min(0)).optional(),
      pageSize: omegaParam(z.number().int().min(1).max(100)).optional(),
      sort: omegaParam(z.enum(AGENCY_ZOOM_LEAD_SORT_VALUES)).optional(),
      order: omegaParam(z.enum(AGENCY_ZOOM_ORDER_VALUES)).optional(),
      status: omegaParam(z.enum(AGENCY_ZOOM_LEAD_STATUS_VALUES)).optional(),
      assignedTo: omegaParam(z.number().int()).optional(),
      leadSourceId: omegaParam(z.number().int()).optional(),
      customerName: omegaString.optional(),
      customerEmail: omegaString.optional(),
      customerPhone: omegaString.optional(),
      startDate: omegaString.optional(),
      endDate: omegaString.optional(),
      soldPeriod: omegaParam(z.enum(AGENCY_ZOOM_LEAD_SOLD_PERIOD_VALUES)).optional(),
      workflowId: omegaParam(z.number().int()).optional(),
      workflowStageId: omegaString.optional(),
      lastActivityEarliestDate: omegaString.optional(),
      lastActivityLatestDate: omegaString.optional(),
    }),
  },

  agencyzoom_get_lead: {
    label: "AgencyZoom Get Lead",
    icon: "user" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Fetch a single AgencyZoom lead by ID",
    hasFailureRoute: true,
    parametersSchema: z.object({
      leadId: omegaParam(z.number().int().positive()),
    }),
  },

  agencyzoom_add_lead_note: {
    label: "AgencyZoom Add Lead Note",
    icon: "note-sticky" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Add a note to an AgencyZoom lead",
    hasFailureRoute: true,
    parametersSchema: z.object({
      leadId: omegaParam(z.number().int().positive()),
      note: omegaString,
    }),
  },

  agencyzoom_search_tasks: {
    label: "AgencyZoom Search Tasks",
    icon: "list-check" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Search AgencyZoom tasks with optional filters",
    hasFailureRoute: true,
    parametersSchema: z.object({
      page: omegaParam(z.number().int().min(0)).optional(),
      pageSize: omegaParam(z.number().int().min(1).max(200)).optional(),
      sort: omegaString.optional(),
      order: omegaParam(z.enum(AGENCY_ZOOM_ORDER_VALUES)).optional(),
      type: omegaParam(z.enum(AGENCY_ZOOM_TASK_CATEGORY_VALUES)).optional(),
      status: omegaParam(z.enum(AGENCY_ZOOM_TASK_STATUS_VALUES)).optional(),
      assigneeId: omegaParam(z.number().int()).optional(),
      period: omegaParam(z.enum(AGENCY_ZOOM_TASK_PERIOD_VALUES)).optional(),
      startDate: omegaString.optional(),
      endDate: omegaString.optional(),
    }),
  },

  agencyzoom_complete_task: {
    label: "AgencyZoom Complete Task",
    icon: "circle-check" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Mark an AgencyZoom task complete",
    hasFailureRoute: true,
    parametersSchema: z.object({
      taskId: omegaParam(z.number().int().positive()),
    }),
  },

  agencyzoom_create_task: {
    label: "AgencyZoom Create Task",
    icon: "plus" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Create an AgencyZoom task",
    hasFailureRoute: true,
    parametersSchema: z.object({
      title: omegaParam(z.string().min(1)),
      dueDatetime: omegaString.optional(),
      comments: omegaString.optional(),
      assigneeId: omegaParam(z.number().int()).optional(),
      contactEmail: omegaString.optional(),
      customerId: omegaParam(z.number().int()).optional(),
      leadId: omegaParam(z.number().int()).optional(),
      duration: omegaParam(z.number().int()).optional(),
      type: omegaParam(z.enum(AGENCY_ZOOM_TASK_CREATE_TYPE_VALUES)).optional(),
      invitees: z.union([z.array(omegaString), omegaCel]).optional(),
      timeSpecific: omegaParam(z.boolean()).optional(),
    }),
  },

  agencyzoom_update_task: {
    label: "AgencyZoom Update Task",
    icon: "pen-to-square" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Update an AgencyZoom task (partial update)",
    hasFailureRoute: true,
    parametersSchema: z.object({
      taskId: omegaParam(z.number().int().positive()),
      title: omegaParam(z.string().min(1)).optional(),
      dueDatetime: omegaString.optional(),
      comments: omegaString.optional(),
      assigneeId: omegaParam(z.number().int()).optional(),
      contactEmail: omegaString.optional(),
      customerId: omegaParam(z.number().int()).optional(),
      leadId: omegaParam(z.number().int()).optional(),
      duration: omegaParam(z.number().int()).optional(),
      type: omegaParam(z.enum(AGENCY_ZOOM_TASK_CREATE_TYPE_VALUES)).optional(),
      invitees: z.union([z.array(omegaString), omegaCel]).optional(),
      timeSpecific: omegaParam(z.boolean()).optional(),
      status: omegaParam(z.enum(AGENCY_ZOOM_TASK_STATUS_VALUES)).optional(),
    }),
  },

  agencyzoom_create_lead: {
    label: "AgencyZoom Create Lead",
    icon: "user-plus" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Create a personal-lines lead in AgencyZoom",
    hasFailureRoute: true,
    parametersSchema: z.object({
      ...agencyZoomLeadSharedShape,
      firstname: omegaParam(z.string().min(1)),
      pipelineId: omegaAgencyZoomSelectedId,
      stageId: omegaAgencyZoomSelectedId,
    }),
  },

  agencyzoom_create_business_lead: {
    label: "AgencyZoom Create Business Lead",
    icon: "building" as IconName,
    iconUrl: "/icons/integrations/agencyzoom.png",
    description: "Create a commercial lead in AgencyZoom",
    hasFailureRoute: true,
    parametersSchema: z.object({
      ...agencyZoomLeadSharedShape,
      pipelineId: omegaAgencyZoomSelectedId,
      stageId: omegaAgencyZoomSelectedId,
      firstname: omegaString.optional(),
      name: omegaString.optional(),
      contactName: omegaString.optional(),
      dba: omegaString.optional(),
      fein: omegaString.optional(),
      businessEntity: omegaString.optional(),
      businessClassification: omegaString.optional(),
      businessClassificationId: omegaParam(z.number().int().positive()).optional(),
      yearBusinessStarted: omegaParam(z.number().int().min(1800).max(2200)).optional(),
      numberOfEmployees: omegaParam(z.number().int().min(0)).optional(),
      annualRevenue: omegaParam(z.number().int().min(0)).optional(),
      payroll: omegaParam(z.number().int().min(0)).optional(),
    }),
  },

  momentum_search_insureds: {
    label: "Momentum Search Insureds",
    icon: "users" as IconName,
    iconUrl: "/icons/integrations/momentum.png",
    description: "Search Momentum insureds and prospects",
    hasFailureRoute: true,
    parametersSchema: z.object({
      name: omegaString.optional(),
      address: omegaString.optional(),
      email: omegaString.optional(),
      phone: omegaString.optional(),
      insuredId: omegaString.optional(),
      customerId: omegaString.optional(),
      databaseId: omegaUuid.optional(),
    }),
  },

  momentum_get_insured: {
    label: "Momentum Get Insured",
    icon: "user" as IconName,
    iconUrl: "/icons/integrations/momentum.png",
    description: "Fetch a single Momentum insured or prospect by database ID",
    hasFailureRoute: true,
    parametersSchema: z.object({
      insuredDatabaseId: omegaUuid.default({ type: "cel", value: "" }),
    }),
  },

  momentum_get_policy: {
    label: "Momentum Get Policy",
    icon: "file-contract" as IconName,
    iconUrl: "/icons/integrations/momentum.png",
    description: "Fetch a single Momentum policy by database ID",
    hasFailureRoute: true,
    parametersSchema: z.object({
      policyDatabaseId: omegaUuid.default({ type: "cel", value: "" }),
    }),
  },

  momentum_add_note: {
    label: "Momentum Add Note",
    icon: "note-sticky" as IconName,
    iconUrl: "/icons/integrations/momentum.png",
    description: "Add a note to a Momentum insured or prospect",
    hasFailureRoute: true,
    parametersSchema: z.object({
      insuredDatabaseId: omegaUuid.default({ type: "cel", value: "" }),
      subject: omegaString,
      type: omegaString.optional(),
    }),
  },

  momentum_create_task: {
    label: "Momentum Create Task",
    icon: "plus" as IconName,
    iconUrl: "/icons/integrations/momentum.png",
    description: "Create a task on a Momentum insured or prospect",
    hasFailureRoute: true,
    parametersSchema: z.object({
      insuredDatabaseId: omegaUuid.default({ type: "cel", value: "" }),
      title: omegaParam(z.string().min(1)),
      ...momentumTaskFieldsShape,
    }),
  },

  momentum_update_task: {
    label: "Momentum Update Task",
    icon: "pen-to-square" as IconName,
    iconUrl: "/icons/integrations/momentum.png",
    description: "Change fields on an existing Momentum task",
    hasFailureRoute: true,
    parametersSchema: z.object({
      taskDatabaseId: omegaUuid.default({ type: "cel", value: "" }),
      insuredDatabaseId: omegaUuid.optional(),
      title: omegaString.optional(),
      ...momentumTaskFieldsShape,
    }),
  },

  momentum_search_tasks: {
    label: "Momentum Search Tasks",
    icon: "list-check" as IconName,
    iconUrl: "/icons/integrations/momentum.png",
    description: "Find Momentum tasks by insured, assignee, status, priority, category, or due date",
    hasFailureRoute: true,
    parametersSchema: z.object({
      insuredDatabaseId: omegaUuid.optional(),
      assigneeAgentId: omegaUuid.optional(),
      status: omegaMomentumTaskStatus.optional(),
      priority: omegaMomentumTaskPriority.optional(),
      categoryName: omegaString.optional(),
      dueFrom: omegaString.optional(),
      dueBefore: omegaString.optional(),
    }),
  },

  fill_pdf_form: {
    label: "Fill PDF Form",
    icon: "file-pen" as IconName,
    description: "Fill an AcroForm PDF using upstream data",
    hasFailureRoute: true,
    parametersSchema: z
      .object({
        template: omegaFilePointer,
        // sourceData is `Value<object>` on the wire and almost always a CEL
        // reference to an upstream node's data (e.g. node.docAnalysis.data
        // or node.parse_csv.data.rows[i]). Lock to CEL like document_analysis.
        sourceData: omegaCel,
        fileName: omegaString,
        hints: omegaString.optional(),
        flatten: omegaParam(z.boolean()).optional(),
      })
      .superRefine((value, ctx) => {
        // Mirror Omega's CheckInput() literal validations. Skip for CEL/template
        // because resolved values are re-validated by `[ValidateResolvedInput]`.
        if (value.fileName.type === "literal") {
          const literalName = (value.fileName.value ?? "").trim();
          if (literalName === "") {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["fileName"],
              message: "'File name' cannot be empty.",
            });
          } else if (!/\.[^./\\]+$/.test(literalName)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["fileName"],
              message:
                "'File name' must include an extension (e.g. filled.pdf).",
            });
          } else if (/[/\\]/.test(literalName)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["fileName"],
              message:
                "'File name' cannot contain path separators (/ or \\).",
            });
          }
        }
        if (value.hints && value.hints.type === "literal") {
          if ((value.hints.value ?? "").trim() === "") {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["hints"],
              message: "'Hints' must not be empty — omit the field instead.",
            });
          }
        }
      }),
  },

  noop: {
    label: "Noop",
    icon: "circle",
    description: "Empty placeholder for branches",
    isPlaceholder: true,
  },

  sub_graph: {
    label: "Sub Graph",
    icon: "object-group" as IconName,
    description: "Group nodes into a reusable nested workflow",
    isContainer: true,
  },

  sub_graph_start: {
    label: "Entry",
    icon: "right-to-bracket" as IconName,
    description: "SubGraph entry point",
    isTrigger: true,
    isPlaceholder: true,
  },

  sub_graph_end: {
    label: "Exit",
    icon: "right-from-bracket" as IconName,
    description: "SubGraph exit point",
    isTerminal: true,
    isPlaceholder: true,
  },

  map: {
    label: "Map",
    icon: "arrow-right-arrow-left" as IconName,
    description:
      "Transform every item of a list with per-item expressions — a For Each plus per-item Data Transform in one step",
    hasFailureRoute: true,
    parametersSchema: z
      .object({
        items: omegaParam(z.array(z.unknown())),
        projection: z.record(omegaParam(z.unknown())).optional(),
      })
      .superRefine((value, ctx) => {
        // Mirror Omega's Map.CheckInput (publish-time validation) so users get
        // immediate feedback instead of a backend rejection on save.
        if (
          value.items.type === "literal" &&
          Array.isArray(value.items.value) &&
          value.items.value.length > MAP_MAX_ITEMS
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["items"],
            message: `'Items' has ${value.items.value.length} items, which exceeds the maximum of ${MAP_MAX_ITEMS}.`,
          });
        }

        if (!value.projection) return;

        const normalizedKeys = new Map<string, string>();
        for (const [key, param] of Object.entries(value.projection)) {
          const normalized = normalizeCelFieldName(key);
          if (!normalized) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["projection", key],
              message: `The projection field name '${key}' normalizes to an empty name.`,
            });
            continue;
          }
          const existing = normalizedKeys.get(normalized);
          if (existing) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["projection", key],
              message: `Projection field collision: '${existing}' and '${key}' are treated as the same output field ('${normalized}'). Rename one of them.`,
            });
          } else {
            normalizedKeys.set(normalized, key);
          }

          // Projection expressions run per item inside the activity with only
          // node.item / node.index in scope — references to other nodes or the
          // run-scoped config/sys/secret namespaces fail at runtime.
          if (param.type !== "cel" && param.type !== "template") continue;
          const expression = String(param.value);
          for (const ref of findForbiddenProjectionReferences(expression)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["projection", key],
              message: `The projection expression for '${key}' references '${ref}', which is not available inside a projection. Projection expressions run once per item and can only read node.item and node.index.`,
            });
          }
        }
      }),
  },

  foreach: {
    label: "For Each",
    icon: "repeat" as IconName,
    description: "Iterate over a collection, running inner graph for each item",
    isContainer: true,
    hasFailureRoute: true,
    parametersSchema: z.object({
      collection: omegaParam(z.array(z.unknown())),
      collectResults: omegaParam(z.boolean()).optional(),
    }),
    containerScope: {
      variables: [
        {
          name: "_each_.item",
          type: "object",
          description: "Current iteration item",
        },
        {
          name: "_each_.index",
          type: "number",
          description: "0-based iteration index",
        },
      ],
    },
  },
};

// =============================================================================
// Derived Exports — NodePicker UI
// =============================================================================

/** Node definition shape used by the NodePicker and other UI components */
export interface NodeDefinitionItem {
  type: string;
  label: string;
  icon: IconName;
  /** Optional brand SVG path; when set, consumers render an <img> instead of the FontAwesome icon. */
  iconUrl?: string;
  description: string;
  superAdminOnly?: boolean;
}

/** All visible node definitions for the NodePicker (excludes noop/ghost) */
export const NODE_DEFINITIONS: NodeDefinitionItem[] = Object.entries(NODE_REGISTRY)
  .filter(([, def]) => !def.isPlaceholder)
  .map(([type, def]) => ({
    type,
    label: def.label,
    icon: def.icon,
    iconUrl: def.iconUrl,
    description: def.description,
    superAdminOnly: def.superAdminOnly,
  }));

/** Node definitions that can be added from handles/edges (excludes Start) */
export const ADDABLE_NODE_DEFINITIONS: NodeDefinitionItem[] = NODE_DEFINITIONS.filter(
  (node) => node.type !== "start"
);

export function getTranslatedNodeDefinitionLabel(type: string, t: TFunction): string {
  switch (type) {
    case NODE_TYPE_KEYS.Start:
      return t("nodeSettings.nodeTypes.start");
    case NODE_TYPE_KEYS.End:
      return t("nodeSettings.nodeTypes.end");
    case NODE_TYPE_KEYS.Delay:
      return t("nodeSettings.nodeTypes.delay");
    case NODE_TYPE_KEYS.RandomNumber:
      return t("nodeSettings.nodeTypes.randomNumber");
    case NODE_TYPE_KEYS.SlackMessage:
      return t("nodeSettings.nodeTypes.slackMessage");
    case "slack_direct_message":
      return t("slackMessage.directMessage");
    case "slack_post_channel_message":
      return t("slackMessage.channelMessage");
    case NODE_TYPE_KEYS.Conditional:
      return t("nodeSettings.nodeTypes.conditional");
    case NODE_TYPE_KEYS.Check:
      return t("nodeSettings.nodeTypes.check");
    case NODE_TYPE_KEYS.OutboundCall:
      return t("nodeSettings.nodeTypes.outboundCall");
    case NODE_TYPE_KEYS.HttpRequest:
      return t("nodeSettings.nodeTypes.httpRequest");
    case NODE_TYPE_KEYS.SendEmail:
      return t("nodeSettings.nodeTypes.sendEmail");
    case NODE_TYPE_KEYS.SendSms:
      return t("nodeSettings.nodeTypes.sendSms");
    case NODE_TYPE_KEYS.DataTransform:
    case "pipe":
      return t("nodeSettings.nodeTypes.dataTransform");
    case NODE_TYPE_KEYS.AddContact:
      return t("nodeSettings.nodeTypes.addContact");
    case NODE_TYPE_KEYS.AddBulkContacts:
      return t("nodeSettings.nodeTypes.addBulkContacts");
    case NODE_TYPE_KEYS.CreateContactList:
      return t("nodeSettings.nodeTypes.createContactList");
    case NODE_TYPE_KEYS.Ghost:
    case "noop":
      return t("nodeSettings.nodeTypes.ghost");
    case NODE_TYPE_KEYS.StartCampaign:
      return t("nodeSettings.nodeTypes.startCampaign");
    case NODE_TYPE_KEYS.CreateCampaign:
      return t("nodeSettings.nodeTypes.createCampaign");
    case NODE_TYPE_KEYS.AddContactToCampaign:
      return t("nodeSettings.nodeTypes.addContactToCampaign");
    case NODE_TYPE_KEYS.AddContactsToCampaignBulk:
      return t("nodeSettings.nodeTypes.addContactsToCampaignBulk");
    case NODE_TYPE_KEYS.AddContactToContactList:
      return t("nodeSettings.nodeTypes.addContactToContactList");
    case NODE_TYPE_KEYS.Chaos:
      return t("nodeSettings.nodeTypes.chaos");
    case NODE_TYPE_KEYS.Agent:
      return t("nodeSettings.nodeTypes.agent");
    case NODE_TYPE_KEYS.RunSkill:
      return t("nodeSettings.nodeTypes.runSkill");
    case NODE_TYPE_KEYS.SubGraph:
      return t("nodeSettings.nodeTypes.subGraph");
    case NODE_TYPE_KEYS.SubGraphStart:
      return t("subGraph.entry");
    case NODE_TYPE_KEYS.SubGraphEnd:
      return t("subGraph.exit");
    case NODE_TYPE_KEYS.ForEach:
      return t("nodeSettings.nodeTypes.forEach");
    case NODE_TYPE_KEYS.DocumentAnalysis:
      return t("nodeSettings.nodeTypes.documentAnalysis");
    case NODE_TYPE_KEYS.JotFormSubmitForm:
      return t("nodeSettings.nodeTypes.jotFormSubmitForm");
    case NODE_TYPE_KEYS.McpCallTool:
      return t("nodeSettings.nodeTypes.mcpCallTool");
    case NODE_TYPE_KEYS.SftpUploadFile:
      return t("nodeSettings.nodeTypes.sftpUploadFile");
    case NODE_TYPE_KEYS.SftpDownloadFile:
      return t("nodeSettings.nodeTypes.sftpDownloadFile");
    case NODE_TYPE_KEYS.SftpListDirectory:
      return t("nodeSettings.nodeTypes.sftpListDirectory");
    case NODE_TYPE_KEYS.GoogleDriveListFiles:
      return t("nodeSettings.nodeTypes.googleDriveListFiles");
    case NODE_TYPE_KEYS.GoogleDriveDownloadFile:
      return t("nodeSettings.nodeTypes.googleDriveDownloadFile");
    case NODE_TYPE_KEYS.GoogleDriveUploadFile:
      return t("nodeSettings.nodeTypes.googleDriveUploadFile");
    case NODE_TYPE_KEYS.GoogleSheetsAppendRows:
      return t("nodeSettings.nodeTypes.googleSheetsAppendRows");
    case NODE_TYPE_KEYS.AgencyZoomSearchCustomers:
      return t("nodeSettings.nodeTypes.agencyZoomSearchCustomers");
    case NODE_TYPE_KEYS.AgencyZoomGetCustomer:
      return t("nodeSettings.nodeTypes.agencyZoomGetCustomer");
    case NODE_TYPE_KEYS.AgencyZoomAddCustomerNote:
      return t("nodeSettings.nodeTypes.agencyZoomAddCustomerNote");
    case NODE_TYPE_KEYS.AgencyZoomSearchLeads:
      return t("nodeSettings.nodeTypes.agencyZoomSearchLeads");
    case NODE_TYPE_KEYS.AgencyZoomGetLead:
      return t("nodeSettings.nodeTypes.agencyZoomGetLead");
    case NODE_TYPE_KEYS.AgencyZoomAddLeadNote:
      return t("nodeSettings.nodeTypes.agencyZoomAddLeadNote");
    case NODE_TYPE_KEYS.AgencyZoomSearchTasks:
      return t("nodeSettings.nodeTypes.agencyZoomSearchTasks");
    case NODE_TYPE_KEYS.AgencyZoomCompleteTask:
      return t("nodeSettings.nodeTypes.agencyZoomCompleteTask");
    case NODE_TYPE_KEYS.AgencyZoomCreateTask:
      return t("nodeSettings.nodeTypes.agencyZoomCreateTask");
    case NODE_TYPE_KEYS.AgencyZoomUpdateTask:
      return t("nodeSettings.nodeTypes.agencyZoomUpdateTask");
    case NODE_TYPE_KEYS.AgencyZoomCreateLead:
      return t("nodeSettings.nodeTypes.agencyZoomCreateLead");
    case NODE_TYPE_KEYS.AgencyZoomCreateBusinessLead:
      return t("nodeSettings.nodeTypes.agencyZoomCreateBusinessLead");
    case NODE_TYPE_KEYS.MomentumSearchInsureds:
      return t("nodeSettings.nodeTypes.momentumSearchInsureds");
    case NODE_TYPE_KEYS.MomentumGetInsured:
      return t("nodeSettings.nodeTypes.momentumGetInsured");
    case NODE_TYPE_KEYS.MomentumGetPolicy:
      return t("nodeSettings.nodeTypes.momentumGetPolicy");
    case NODE_TYPE_KEYS.MomentumAddNote:
      return t("nodeSettings.nodeTypes.momentumAddNote");
    case NODE_TYPE_KEYS.MomentumCreateTask:
      return t("nodeSettings.nodeTypes.momentumCreateTask");
    case NODE_TYPE_KEYS.MomentumUpdateTask:
      return t("nodeSettings.nodeTypes.momentumUpdateTask");
    case NODE_TYPE_KEYS.MomentumSearchTasks:
      return t("nodeSettings.nodeTypes.momentumSearchTasks");
    case NODE_TYPE_KEYS.CreateFile:
      return t("nodeSettings.nodeTypes.createFile");
    case NODE_TYPE_KEYS.ParseCsv:
      return t("nodeSettings.nodeTypes.parseCsv");
    case NODE_TYPE_KEYS.ParseExcel:
      return t("nodeSettings.nodeTypes.parseExcel");
    case NODE_TYPE_KEYS.SliceCsv:
      return t("nodeSettings.nodeTypes.sliceCsv");
    case NODE_TYPE_KEYS.CreateCsv:
      return t("nodeSettings.nodeTypes.createCsv");
    case NODE_TYPE_KEYS.GetCallData:
      return t("nodeSettings.nodeTypes.getCallData");
    case NODE_TYPE_KEYS.DownloadCallRecording:
      return t("nodeSettings.nodeTypes.downloadCallRecording");
    case NODE_TYPE_KEYS.WebSearch:
      return t("nodeSettings.nodeTypes.webSearch");
    case NODE_TYPE_KEYS.WebExtract:
      return t("nodeSettings.nodeTypes.webExtract");
    case NODE_TYPE_KEYS.FillPdfForm:
      return t("nodeSettings.nodeTypes.fillPdfForm");
    case NODE_TYPE_KEYS.Map:
      return t("nodeSettings.nodeTypes.map");
    default:
      return t("nodeSettings.nodeTypes.node");
  }
}

// =============================================================================
// OmegaParameter helpers
// =============================================================================

/**
 * Check if a ZodUnion represents an OmegaParameter (literal | cel | template).
 * Returns the literal option if it is, undefined otherwise.
 */
function findOmegaParamLiteralOption(
  options: readonly z.ZodTypeAny[]
): z.ZodObject<z.ZodRawShape> | undefined {
  if (options.length !== 3) return undefined;

  const hasTypedObject = options.some(
    (o) => o instanceof z.ZodObject && "type" in (o as z.ZodObject<z.ZodRawShape>).shape
  );
  if (!hasTypedObject) return undefined;

  return options.find((o) => {
    if (!(o instanceof z.ZodObject)) return false;
    const shape = (o as z.ZodObject<z.ZodRawShape>).shape;
    if (!("type" in shape) || !("value" in shape)) return false;
    return shape.type instanceof z.ZodLiteral && shape.type.value === "literal";
  }) as z.ZodObject<z.ZodRawShape> | undefined;
}

/**
 * All valid node types (including start, end, noop).
 */
export function getAllNodeTypes(): string[] {
  return Object.keys(NODE_REGISTRY);
}

// =============================================================================
// Derived Exports — Validation Helpers
// =============================================================================

/** Check if a node type string is valid */
export function isValidNodeType(type: string): boolean {
  return type in NODE_REGISTRY;
}

/** Get all node types that have failure routes — derived from registry, not hardcoded. */
export function getFailureRouteTypes(): string[] {
  return Object.entries(NODE_REGISTRY)
    .filter(([, def]) => def.hasFailureRoute)
    .map(([type]) => type);
}

/** Get all branching node types — derived from registry, not hardcoded. */
export function getBranchingTypes(): string[] {
  return Object.entries(NODE_REGISTRY)
    .filter(([, def]) => def.isBranching)
    .map(([type]) => type);
}

/** Check if a node type has a failure route (onFailureEdgeId) */
export function nodeHasFailureRoute(type: string): boolean {
  return NODE_REGISTRY[type]?.hasFailureRoute ?? false;
}

/** Check if a node type is a branching node (split) */
export function nodeIsBranching(type: string): boolean {
  return NODE_REGISTRY[type]?.isBranching ?? false;
}

/** Check if a node type is a trigger (start) */
export function nodeIsTrigger(type: string): boolean {
  return NODE_REGISTRY[type]?.isTrigger ?? false;
}

/** Check if a node type is a terminal (end) */
export function nodeIsTerminal(type: string): boolean {
  return NODE_REGISTRY[type]?.isTerminal ?? false;
}

/** Check if a node type is a container (sub_graph) */
export function nodeIsContainer(type: string): boolean {
  return NODE_REGISTRY[type]?.isContainer ?? false;
}

/**
 * Return the `containerScope` declaration for a container type, or
 * `undefined` if the type is not a container or doesn't expose
 * iteration-scope variables. Drives the builder UI's available-variables
 * injection.
 */
export function getContainerScope(type: string): ContainerScope | undefined {
  return NODE_REGISTRY[type]?.containerScope;
}

/**
 * All registered container types that expose iteration-scope variables.
 * Used by the system prompt to enumerate "iteration-injecting containers"
 * generically without naming `foreach` directly.
 */
export function getIterationContainerTypes(): string[] {
  return Object.entries(NODE_REGISTRY)
    .filter(([, def]) => def.containerScope?.variables.length)
    .map(([type]) => type);
}

/** Get the default label for a node type */
export function getNodeLabel(type: string): string {
  return NODE_REGISTRY[type]?.label ?? type;
}

// =============================================================================
// Derived Exports — Default Parameter Generation
// =============================================================================

/**
 * Resolve a default raw value for a Zod schema. Mirrors generateDefaultValue's
 * intent and produces a concrete value for every non-optional schema kind we
 * use — used for OmegaParameter literal payloads, where the value field is
 * required.
 *
 * Returns `undefined` only for ZodOptional, which signals the caller (the
 * ZodObject branch below) to omit the field from the default object entirely.
 * That preserves "field is genuinely optional" semantics rather than
 * inventing a fake default for it.
 */
function defaultRawValueForSchema(schema: z.ZodTypeAny): unknown {
  if (schema instanceof z.ZodDefault) return schema._def.defaultValue();
  if (schema instanceof z.ZodOptional) return undefined;
  if (schema instanceof z.ZodNullable) return null;
  if (schema instanceof z.ZodNumber) return 0;
  if (schema instanceof z.ZodBoolean) return false;
  if (schema instanceof z.ZodArray) return [];
  if (schema instanceof z.ZodRecord) return {};
  if (schema instanceof z.ZodObject) {
    const result: Record<string, unknown> = {};
    for (const [key, fieldSchema] of Object.entries(schema.shape)) {
      const v = defaultRawValueForSchema(fieldSchema as z.ZodTypeAny);
      if (v !== undefined) result[key] = v;
    }
    return result;
  }
  if (schema instanceof z.ZodString) {
    const checks = (schema._def as { checks?: Array<{ kind?: string }> }).checks;
    if (checks?.some((c) => c.kind === "uuid")) {
      return "00000000-0000-0000-0000-000000000000";
    }
    return "";
  }
  return "";
}

/**
 * Resolve the default literal value for an OmegaParameter's value schema.
 */
function defaultLiteralForValueSchema(valueSchema: z.ZodTypeAny): {
  type: "literal";
  value: unknown;
} {
  return { type: "literal", value: defaultRawValueForSchema(valueSchema) };
}

/**
 * If the schema is an OmegaParameter union (literal | cel | template),
 * return a default `{ type: "literal", value: ... }`.
 */
function defaultForOmegaParamUnion(schema: z.ZodUnion<z.ZodUnionOptions>): unknown {
  const options = schema.options as readonly z.ZodTypeAny[];
  const literalOption = findOmegaParamLiteralOption(options);
  if (!literalOption) return undefined;
  return defaultLiteralForValueSchema(literalOption.shape.value);
}

/**
 * Generate default Omega parameter values for a Zod field schema.
 *
 * - OmegaParameter unions → `{ type: "literal", value: "" | 0 | false }`
 * - Optional fields → `undefined` (skipped)
 * - Nested objects → recurse
 * - Primitives → empty/zero defaults
 */
function generateDefaultValue(schema: z.ZodTypeAny): unknown {
  if (schema instanceof z.ZodOptional) return undefined;
  if (schema instanceof z.ZodDefault) return schema._def.defaultValue();
  if (schema instanceof z.ZodLiteral) return schema.value;
  if (schema instanceof z.ZodUnion) {
    // ValueList<T> dual form (`z.union([z.array(...), omegaCel])`): default to
    // an empty list (list mode) rather than the OmegaParameter literal path.
    const options = schema.options as readonly z.ZodTypeAny[];
    if (options.some((opt) => opt instanceof z.ZodArray)) return [];
    return defaultForOmegaParamUnion(schema);
  }
  if (schema instanceof z.ZodArray) return [];
  if (schema instanceof z.ZodRecord) return {};
  if (schema instanceof z.ZodString) return "";
  if (schema instanceof z.ZodNumber) return 0;
  if (schema instanceof z.ZodBoolean) return false;

  if (schema instanceof z.ZodObject) {
    return generateDefaultsFromShape(schema.shape);
  }

  return undefined;
}

/**
 * Walk a ZodObject shape and generate default values for each field.
 */
function generateDefaultsFromShape(shape: z.ZodRawShape): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, fieldSchema] of Object.entries(shape)) {
    const val = generateDefaultValue(fieldSchema);
    if (val !== undefined) {
      result[key] = val;
    }
  }
  return result;
}

/**
 * Auto-generate default Omega-format parameters for a node type by
 * introspecting its Zod parametersSchema.
 *
 * Returns `undefined` for node types without a schema (end, noop).
 *
 * @example
 *   getDefaultParametersForType("random_number")
 *   // → { min: { type: "literal", value: 0 }, max: { type: "literal", value: 0 } }
 *
 *   getDefaultParametersForType("send_email")
 *   // → { to: { type: "literal", value: "" }, subject: { type: "literal", value: "" }, body: { type: "literal", value: "" } }
 */
export function getDefaultParametersForType(type: string): Record<string, unknown> | undefined {
  const entry = NODE_REGISTRY[type];
  if (!entry?.parametersSchema) return undefined;

  return generateDefaultsFromShape(unwrapParametersShape(entry.parametersSchema));
}

/**
 * parametersSchema may be a bare ZodObject or a ZodEffects wrapping one
 * (when superRefine is used). Both expose the inner shape, but only via
 * different paths — ZodObject directly, ZodEffects through innerType().
 */
function unwrapParametersShape(
  schema: z.ZodObject<z.ZodRawShape> | z.ZodEffects<z.ZodObject<z.ZodRawShape>>
): z.ZodRawShape {
  if (schema instanceof z.ZodEffects) {
    return schema.innerType().shape;
  }
  return schema.shape;
}
